import { useEffect } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { MiControlledTextField, MiModal } from "@miplatform/components";
import { ButtonVariants, Colors } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiChartSankeyVariant } from "@mdi/js";
import { useTheme } from "@mui/material";

const createAutoTriggerSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Flow Name is required.")
    .max(255, "Name must be less than 255 characters."),
});

export interface CreateAutoTriggerFormValues {
  name: string;
}

export interface CreateAutoTriggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAutoTriggerFormValues) => void;
  isLoading?: boolean;
}

export const CreateAutoTriggerModal: React.FC<CreateAutoTriggerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<CreateAutoTriggerFormValues>({
    resolver: zodResolver(createAutoTriggerSchema),
    defaultValues: {
      name: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <MiModal
      title="New Auto-Trigger Flow"
      titleIcon={mdiChartSankeyVariant}
      leftIconColor={theme.palette.primary.main}
      open={isOpen}
      closeModal={onClose}
      loading={isLoading}
      description="Name your new order Auto-Trigger Flow"
      actions={[
        {
          name: "Cancel",
          variant: ButtonVariants.Text,
          color: Colors.Black,
          onClick: onClose,
        },
        {
          name: "Create New Flow",
          variant: ButtonVariants.Contained,
          disabled: !isValid || isLoading,
          onClick: handleSubmit(onSubmit),
        },
      ]}
    >
      <MiControlledTextField
        name="name"
        label="Flow Name"
        formControl={control}
      />
    </MiModal>
  );
};
